import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Github } from "lucide-react";
import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useTranslatedProjectData, useTranslatedPageData } from "../utils/projectTranslations";
import { useNavigation } from "../contexts/NavigationContext";

const ProjectDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { goBackToPrevious } = useNavigation();
  const translatedData = useTranslatedProjectData();
  const translatedPageData = useTranslatedPageData();

  const handleBackClick = () => {
    goBackToPrevious(navigate);
  };

  // Find project in both translated data sources
  const rawProject = [...translatedData, ...translatedPageData].find(p => p.id === id);
  
  // Normalize project data to handle different field structures
  const project = rawProject ? {
    ...rawProject,
    // Map pageData fields to data.js field names
    img: rawProject.img || rawProject.image,
    desc: rawProject.desc || rawProject.description,
    demoUrl: rawProject.demoUrl || rawProject.live,
    githubUrl: rawProject.githubUrl || rawProject.github,
  } : null;
  
  if (!project) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-lighterSlate mb-4">{t('common.error')}</h1>
          <button 
            onClick={handleBackClick}
            className="px-6 py-3 border border-accent text-accent rounded font-mono text-sm hover:bg-accent/10 transition-all duration-300"
          >
            {t('projectDetail.backToProjects')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy text-white">
      <Navbar />
      
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={handleBackClick}
            className="flex items-center text-accent hover:text-lighterSlate transition-colors duration-300 mb-8"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('projectDetail.backToProjects')}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-lighterSlate mb-4">
                {project.title}
              </h1>
              
              <p className="text-slate text-lg mb-6">
                {project.desc}
              </p>

              <div className="flex gap-4 mb-8">
                {project.demoUrl && (
                  <a
                    href={project.demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-accent text-navy rounded-md font-medium hover:bg-accent/90 transition-all duration-300"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('projectDetail.liveDemo')}
                  </a>
                )}
                
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 border border-accent text-accent rounded-md font-medium hover:bg-accent/10 transition-all duration-300"
                  >
                    <Github className="w-4 h-4 mr-2" />
                    {t('projectDetail.sourceCode')}
                  </a>
                )}
              </div>

              {project.techStack && (
                <div>
                  <h3 className="text-xl font-bold text-lighterSlate mb-4">{t('projectDetail.techStack')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {project.techStack.map((tech, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-lightNavy border border-accent/20 rounded text-sm font-mono text-slate"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <img
                src={project.img}
                alt={project.title}
                className="w-full h-auto object-cover rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectDetail;